import React, { useState, useEffect } from 'react'
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts'
import { C, Track, Spinner, Empty, Mono } from '../components/ui'
import { useFetch } from '../hooks/useFetch'
import { getDailyScorecard, getResultsDaily, todayStr } from '../api/client'

const PROP_LABELS = {
  'HRR_over_1.5': 'HRR Over 1.5',
  'total_bases':  'Total Bases',
  'HR_anytime':   'Home Run',
  'doubles':      'Doubles',
  'strikeouts':   'Strikeouts',
}
const PROP_ICONS = {
  'HRR_over_1.5': '📊', 'total_bases': '🔥', 'HR_anytime': '💣', 'strikeouts': '⚡', 'doubles': '2️⃣'
}

function prevDate(d) {
  const dt = new Date(d + 'T12:00:00'); dt.setDate(dt.getDate() - 1); return dt.toISOString().split('T')[0]
}
function nextDate(d) {
  const dt = new Date(d + 'T12:00:00'); dt.setDate(dt.getDate() + 1); return dt.toISOString().split('T')[0]
}

// ── Player Results Drilldown ──────────────────────────────────────────────────
function MarketDetail({ mkt, date, onClose }) {
  const label = PROP_LABELS[mkt.prop_type] || mkt.prop_type
  const icon  = PROP_ICONS[mkt.prop_type]  || '📊'
  const hr10  = mkt.hit_rate_top_10 || 0
  const color = hr10 >= 0.65 ? C.green : hr10 >= 0.52 ? C.blue : C.red

  const [results,  setResults]  = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [filter,   setFilter]   = useState('all')
  const [tier, setTier] = useState('top10')

  useEffect(() => {
    setLoading(true)
    getResultsDaily(date, mkt.prop_type, tier)
      .then(d => setResults(d))
      .catch(() => setResults(null))
      .finally(() => setLoading(false))
  }, [date, mkt.prop_type, tier])

  const detail = results?.players || []
  const allPlayers = detail.filter(p => {
    if (filter === 'hit')  return p.result === 'HIT'
    if (filter === 'miss') return p.result !== 'HIT'
    return true
  })

  const hits   = detail.filter(p => p.result === 'HIT').length
  const misses = detail.length - hits

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 300, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ width: '100%', maxWidth: 430, margin: '0 auto', background: C.bk2, borderRadius: '16px 16px 0 0', padding: '20px 16px 40px', border: `1px solid ${C.border}`, maxHeight: '88vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 18 }}>{icon} {label}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ fontSize: 10, color: C.dim, marginBottom: 16 }}>{date}</div>

        {/* Hero hit rate */}
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 48, color, lineHeight: 1, marginBottom: 4 }}>
          {(hr10 * 100).toFixed(0)}%
        </div>
        <div style={{ fontSize: 11, color: C.dim, marginBottom: 16 }}>Top 10 hit rate · {mkt.sample_size || 0} plays settled</div>

        {/* Tier grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { l: 'Top 10 Plays', v: mkt.hit_rate_top_10,  n: Math.round((mkt.sample_size || 0) * 0.3),  c: color   },
            { l: 'Top 25 Plays', v: mkt.hit_rate_top_25,  n: Math.round((mkt.sample_size || 0) * 0.7),  c: C.blue  },
            { l: 'All Plays',    v: mkt.hit_rate_all,     n: mkt.sample_size || 0,                       c: C.dim   },
            { l: 'High Conf',    v: mkt.hit_rate_whale,   n: Math.round((mkt.sample_size || 0) * 0.15), c: C.green },
          ].map((t, i) => (
            <div key={i} style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 22, color: t.c }}>
                {t.v != null ? `${(t.v * 100).toFixed(0)}%` : '—'}
              </div>
              <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{t.l}</div>
              <div style={{ fontSize: 9, color: C.dim, marginTop: 1 }}>{t.n} plays</div>
            </div>
          ))}
        </div>

        {/* ROI + Cal gap */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <div style={{ flex: 1, background: (mkt.roi_top_10 || 0) > 0 ? `${C.green}15` : `${C.red}15`, border: `1px solid ${(mkt.roi_top_10 || 0) > 0 ? `${C.green}30` : `${C.red}30`}`, borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 20, color: (mkt.roi_top_10 || 0) > 0 ? C.green : C.red }}>
              {(mkt.roi_top_10 || 0) > 0 ? '+' : ''}{(mkt.roi_top_10 || 0).toFixed(2)}u
            </div>
            <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>ROI (Top 10)</div>
          </div>
          <div style={{ flex: 1, background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 20, color: C.blue }}>
              {((mkt.calibration_gap || 0) * 100).toFixed(1)}%
            </div>
            <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>Calibration Gap</div>
          </div>
        </div>

        <Track pct={hr10 * 100} color={color} height={6} />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, marginBottom: 20 }}>
          <Mono size={9} color={C.dim}>Baseline 50%</Mono>
          <Mono size={9} color={color}>Model {(hr10 * 100).toFixed(0)}%</Mono>
        </div>

        {/* Tier selector */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[['top10','Top 10'],['top25','Top 25'],['all','All']].map(([t,l]) => (
            <button key={t} onClick={() => setTier(t)} style={{ flex: 1, background: tier === t ? C.blueBg : 'none', border: `1px solid ${tier === t ? C.blueBdr : C.border}`, borderRadius: 8, padding: '6px 4px', cursor: 'pointer', fontSize: 10, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, color: tier === t ? C.blue : C.dim, textTransform: 'uppercase' }}>{l}</button>
          ))}
        </div>
        {/* Individual player results */}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase' }}>
              {tier === 'top10' ? 'TOP 10' : tier === 'top25' ? 'TOP 25' : 'ALL'} PLAYER RESULTS
            </div>
            {detail.length > 0 && (
              <div style={{ display: 'flex', gap: 4 }}>
                {['all','hit','miss'].map(f => (
                  <button key={f} onClick={() => setFilter(f)} style={{ background: filter === f ? C.blueBg : 'none', border: `1px solid ${filter === f ? C.blueBdr : C.border}`, borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 9, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, color: filter === f ? C.blue : C.dim, textTransform: 'uppercase' }}>{f}</button>
                ))}
              </div>
            )}
          </div>

          {/* Hit/miss summary */}
          {detail.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <div style={{ flex: 1, textAlign: 'center', background: `${C.green}15`, border: `1px solid ${C.green}30`, borderRadius: 8, padding: '8px 4px' }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 22, color: C.green }}>{hits}</div>
                <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>✅ Hits</div>
              </div>
              <div style={{ flex: 1, textAlign: 'center', background: `${C.red}15`, border: `1px solid ${C.red}30`, borderRadius: 8, padding: '8px 4px' }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 22, color: C.red }}>{misses}</div>
                <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>❌ Misses</div>
              </div>
              <div style={{ flex: 1, textAlign: 'center', background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 4px' }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 22, color }}>
                  {detail.length > 0 ? `${Math.round((hits / detail.length) * 100)}%` : '—'}
                </div>
                <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>Rate</div>
              </div>
            </div>
          )}

          {loading && <Spinner />}

          {!loading && detail.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px', color: C.dim, fontSize: 12 }}>
              No individual results data for this date yet
            </div>
          )}

          {!loading && allPlayers.map((p, i) => {
            const hit   = p.result === 'HIT'
            const color = hit ? C.green : C.red
            const actual = p.actual_stat != null ? parseFloat(p.actual_stat).toFixed(1) : '—'
            const pred   = p.pred || '—'
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: `1px solid ${C.dim2}`, background: hit ? `${C.green}06` : `${C.red}04` }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: `${color}18`, border: `1px solid ${color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
                  {hit ? '✅' : '❌'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.player}</div>
                  <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{p.team} · {PROP_LABELS[p.prop_type] || p.prop_type} O{p.line}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, fontWeight: 700, color }}>
                    {actual}
                  </div>
                  <div style={{ fontSize: 9, color: C.dim, marginTop: 1 }}>pred {pred}</div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── Market Row Card ───────────────────────────────────────────────────────────
function MarketCard({ mkt, onClick }) {
  const hr10  = mkt.hit_rate_top_10 || 0
  const color = hr10 >= 0.65 ? C.green : hr10 >= 0.52 ? C.blue : C.red
  const label = PROP_LABELS[mkt.prop_type] || mkt.prop_type
  const icon  = PROP_ICONS[mkt.prop_type]  || '📊'
  return (
    <div onClick={onClick} style={{ background: C.bk2, border: `1px solid ${C.border}`, borderLeft: `3px solid ${color}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10, cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 16 }}>{icon} {label}</div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: C.dim, marginTop: 2 }}>{mkt.sample_size} plays · tap to see who hit</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 28, color, lineHeight: 1 }}>{(hr10 * 100).toFixed(0)}%</div>
          <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>Top 10 Hit %</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        {[
          { l: 'All',       v: mkt.hit_rate_all,   c: C.dim   },
          { l: 'Top 10',    v: hr10,                c: color   },
          { l: 'Top 25',    v: mkt.hit_rate_top_25, c: C.blue  },
          { l: 'High Conf', v: mkt.hit_rate_whale,  c: C.green },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 14, fontWeight: 700, color: s.c }}>{((s.v || 0) * 100).toFixed(0)}%</div>
            <div style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginTop: 2 }}>{s.l}</div>
          </div>
        ))}
      </div>
      <Track pct={hr10 * 100} color={color} height={4} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <Mono size={9} color={C.dim}>Baseline 50%</Mono>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ background: (mkt.roi_top_10 || 0) > 0 ? `${C.green}15` : `${C.red}15`, border: `1px solid ${(mkt.roi_top_10 || 0) > 0 ? `${C.green}30` : `${C.red}30`}`, borderRadius: 5, padding: '1px 6px', fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: (mkt.roi_top_10 || 0) > 0 ? C.green : C.red }}>
            {(mkt.roi_top_10 || 0) > 0 ? '+' : ''}{(mkt.roi_top_10 || 0).toFixed(2)}u ROI
          </span>
          <Mono size={9} color={color}>Model {(hr10 * 100).toFixed(0)}%</Mono>
        </div>
      </div>
    </div>
  )
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: C.bk3, border: `1px solid ${C.blueBdr}`, borderRadius: 8, padding: '8px 12px', fontSize: 11 }}>
      <div style={{ color: C.dim, marginBottom: 2 }}>{label}</div>
      {payload.map((p, i) => <div key={i} style={{ color: p.color, fontWeight: 600 }}>{p.name}: {p.value}%</div>)}
    </div>
  )
}

const CHART_DATA = [
  { date: '4/8', hrr: 55 }, { date: '4/9', hrr: 60 }, { date: '4/10', hrr: 65 },
  { date: '4/11', hrr: 40 }, { date: '4/12', hrr: 100 }, { date: '4/13', hrr: 70 },
]

export default function SigmaTab() {
  const [date,   setDate]   = useState(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().split('T')[0] })
  const [detail, setDetail] = useState(null)

  const { data, loading, error } = useFetch(() => getDailyScorecard(date), [date])
  const markets   = data?.markets || []
  const hrrMarket = markets.find(m => m.prop_type === 'HRR_over_1.5')

  return (
    <div style={{ paddingBottom: 84 }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: `linear-gradient(180deg, ${C.bk} 85%, transparent)`, padding: '16px 16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: C.blue, fontSize: 22, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>📋</span>
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 22 }}>Historical Results</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => setDate(prevDate(date))} style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 6, padding: '5px 10px', color: C.dim, cursor: 'pointer', fontSize: 14 }}>‹</button>
            <input type="date" value={date} max={todayStr()} onChange={e => setDate(e.target.value)} style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 8px', color: C.text, fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }} />
            <button onClick={() => { if (date < todayStr()) setDate(nextDate(date)) }} style={{ background: C.bk3, border: `1px solid ${C.border}`, borderRadius: 6, padding: '5px 10px', color: date < todayStr() ? C.dim : C.bk3, cursor: 'pointer', fontSize: 14 }}>›</button>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px' }}>
        {hrrMarket && (
          <div onClick={() => setDetail(hrrMarket)} style={{ background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${C.blue}22 0%, transparent 70%), linear-gradient(180deg, ${C.bk3} 0%, ${C.bk2} 100%)`, border: `1px solid ${C.blueBdr}`, borderRadius: 16, padding: '16px', marginBottom: 14, boxShadow: `0 0 24px ${C.blue}18`, cursor: 'pointer' }}>
            <div style={{ fontSize: 11, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.08em', marginBottom: 8 }}>HRR RESULTS · {date}</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 900, fontSize: 48, color: C.blue, lineHeight: 1 }}>{((hrrMarket.hit_rate_top_10 || 0) * 100).toFixed(0)}%</div>
                <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>Top 10 Hit Rate</div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 13, color: C.green, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>+{(((hrrMarket.hit_rate_top_10 || 0) - 0.5) * 100).toFixed(0)}pp vs baseline</div>
                <div style={{ fontSize: 10, color: C.blue, marginTop: 2 }}>tap to see who hit ›</div>
              </div>
            </div>
            <Track pct={(hrrMarket.hit_rate_top_10 || 0) * 100} color={C.blue} height={6} shimmer />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
              <Mono size={9} color={C.dim}>BASELINE 50%</Mono>
              <Mono size={9} color={C.blue}>MODEL {((hrrMarket.hit_rate_top_10 || 0) * 100).toFixed(0)}%</Mono>
            </div>
          </div>
        )}

        <div style={{ background: C.bk2, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 8px 10px', marginBottom: 14 }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 11, color: C.dim, letterSpacing: '.08em', textTransform: 'uppercase', padding: '0 8px 10px' }}>HRR Hit Rate — Last 7 Slates</div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={CHART_DATA} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
              <defs>
                <linearGradient id="hrrGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={C.blue} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={C.blue} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: C.dim, fontSize: 9, fontFamily: 'IBM Plex Mono' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: C.dim, fontSize: 9, fontFamily: 'IBM Plex Mono' }} tickLine={false} axisLine={false} domain={[0, 100]} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="hrr" name="HRR" stroke={C.blue} strokeWidth={2} fill="url(#hrrGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {loading  && <Spinner />}
        {error    && <Empty message="No results data — run validation first" />}
        {!loading && !error && markets.length === 0 && <Empty message={`No results for ${date} yet`} />}
        {!loading && !error && markets.map((mkt, i) => (
          <MarketCard key={mkt.prop_type || i} mkt={mkt} onClick={() => setDetail(mkt)} />
        ))}
      </div>

      {detail && <MarketDetail mkt={detail} date={date} onClose={() => setDetail(null)} />}
    </div>
  )
}
